/**
 * TikTok Pixel Service - Multi-instance aware.
 * Loads the TikTok script and instantiates one pixel per registered ID via
 * window.ttq.load + ttq.instance(pixelId).track().
 */

function ensureTtqStub() {
  if (window.ttq) return;
  const w = window;
  const ttq = (w.ttq = w.ttq || []);
  ttq.methods = [
    'page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once',
    'ready', 'alias', 'group', 'enableCookie', 'disableCookie', 'holdConsent',
    'revokeConsent', 'grantConsent',
  ];
  ttq.setAndDefer = function (target, method) {
    target[method] = function () {
      target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
    };
  };
  for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
  ttq.instance = function (id) {
    const inst = ttq._i[id] || [];
    for (let j = 0; j < ttq.methods.length; j++) ttq.setAndDefer(inst, ttq.methods[j]);
    return inst;
  };
  ttq.load = function (id, opts) {
    const url = 'https://analytics.tiktok.com/i18n/pixel/events.js';
    ttq._i = ttq._i || {};
    ttq._i[id] = [];
    ttq._i[id]._u = url;
    ttq._t = ttq._t || {};
    ttq._t[id] = +new Date();
    ttq._o = ttq._o || {};
    ttq._o[id] = opts || {};
    if (!document.getElementById('tiktok-pixel-script')) {
      const script = document.createElement('script');
      script.id = 'tiktok-pixel-script';
      script.async = true;
      script.src = `${url}?sdkid=${id}&lib=ttq`;
      document.head.appendChild(script);
    }
  };
}

class TikTokPixelService {
  constructor() {
    this.pixelIds = new Set();
    this.isEnabled = true;
  }

  registerPixel(pixelId, { trackPageView = true } = {}) {
    if (!pixelId) return;
    if (this.pixelIds.has(pixelId)) return;
    ensureTtqStub();
    window.ttq.load(pixelId);
    this.pixelIds.add(pixelId);
    if (trackPageView) {
      window.ttq.instance(pixelId).page();
    }
    console.log('[TIKTOK PIXEL] registered:', pixelId);
  }

  setEnabled(enabled) {
    this.isEnabled = !!enabled;
  }

  _trackOn(pixelId, eventName, data, eventId) {
    if (!pixelId || !this.isEnabled || !window.ttq) return null;
    const inst = window.ttq.instance(pixelId);
    inst.track(eventName, data, { event_id: eventId });
    return eventId;
  }

  trackViewContent(pixelId, product) {
    if (!product || !product.id) return null;
    return this._trackOn(pixelId, 'ViewContent', {
      contents: [{ content_id: `birk_v2_${product.id}`, content_type: 'product', content_name: product.name }],
      value: product.price || 0,
      currency: 'DZD',
    });
  }

  trackInitiateCheckout(pixelId, items, value) {
    if (!items || items.length === 0) return null;
    return this._trackOn(pixelId, 'InitiateCheckout', {
      contents: items.map((it) => ({
        content_id: `birk_v2_${it.id || it.productId}`,
        content_type: 'product',
        quantity: it.quantity || 1,
      })),
      value: value || 0,
      currency: 'DZD',
    });
  }

  trackPurchase(pixelId, orderData, eventId) {
    if (!orderData || !orderData.items || orderData.items.length === 0) return null;
    const id = eventId || orderData.eventId;
    return this._trackOn(
      pixelId,
      'CompletePayment',
      {
        contents: orderData.items.map((it) => ({
          content_id: `birk_v2_${it.id || it.productId}`,
          content_type: 'product',
          quantity: it.quantity || 1,
        })),
        value: orderData.total || orderData.subtotal || 0,
        currency: 'DZD',
        order_id: orderData.orderNumber || orderData.orderId,
      },
      id
    );
  }
}

export default new TikTokPixelService();
